/**
 * Creates and appends an inline script element to the document.
 *
 * @param text - The JavaScript code to be included in the script element.
 * @returns The created HTMLScriptElement.
 */
export function inlineScript(text: string): HTMLScriptElement {
  const script = document.createElement("script");
  script.textContent = text;
  document.head.appendChild(script);
  return script;
}

/**
 * Loads a script from a given source URL and returns a promise that resolves
 * with the HTMLScriptElement once the script is successfully loaded.
 *
 * @param src - The source URL of the script to load.
 * @returns A promise that resolves with the HTMLScriptElement when the script is loaded.
 */
export function loadScript(src: string): Promise<HTMLScriptElement> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");

    script.onerror = () => {
      const error = new Error("Failed to load script.");
      reject(error);
    };

    script.onload = () => {
      resolve(script);
    };

    script.src = src;
    document.head.appendChild(script);
  });
}

export function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export function onDomReady(fn: () => void) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  } else {
    fn();
  }
}
